'use client';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { Clock, Undo, ArrowDown, PersonLock, MonitorSmartphone, ShieldAlert, TicketPercentage } from '@/public/images/icons/icons';
import { fadeInUp, viewportOnce } from '@/lib/animations';

export default function Comparison() {
  const t = useTranslations('Merchants.Comparison');

  const rows = [
    {
      feature_key: 'settlement_speed',
      card_payment_key: 'card_settlement',
      kuik_payment_key: 'kuik_settlement',
      kuik_highlight: true,
      icon: <Clock width={15} height={15} className="shrink-0" />,
    },
    {
      feature_key: 'fee',
      card_payment_key: 'card_fee',
      kuik_payment_key: 'kuik_fee',
      kuik_highlight: true,
      icon: <Undo width={15} height={15} className="shrink-0" />,
    },
    {
      feature_key: 'chargeback_risk',
      card_payment_key: 'yes',
      kuik_payment_key: 'no_A2A',
      kuik_highlight: true,
      icon: <ArrowDown width={15} height={15} className="shrink-0" />,
    },
    {
      feature_key: 'terminal_required',
      card_payment_key: 'yes_pos',
      kuik_payment_key: 'no_qr_only',
      kuik_highlight: true,
      icon: <PersonLock width={15} height={15} className="shrink-0" />,
    },
    {
      feature_key: 'customer_data',
      card_payment_key: 'cardData_exchanged',
      kuik_payment_key: 'none_no_card_data',
      kuik_highlight: true,
      icon: <MonitorSmartphone width={15} height={15} className="shrink-0" />,
    },
    {
      feature_key: 'funds_credited',
      card_payment_key: 'next_business_day',
      kuik_payment_key: 'instantly_247',
      kuik_highlight: true,
      icon: <ShieldAlert width={15} height={15} className="shrink-0" />,
    },
    {
      feature_key: 'refunds',
      card_payment_key: 'complex_mediated',
      kuik_payment_key: 'direct_instant',
      kuik_highlight: true,
      icon: <TicketPercentage width={15} height={15} className="shrink-0" />,
    },
  ];

  return (
    <motion.section
      aria-labelledby="comparison-heading"
      className="w-full bg-white py-6 sm:py-12 px-6 sm:px-10 lg:px-16"
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      variants={fadeInUp}
    >
      <div className="max-w-8xl mx-auto bg-white rounded-3xl p-8 sm:p-12 border border-slate-100 shadow-xl grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-10 items-center">
        <div className="lg:col-span-4 space-y-4">
          <span className="text-xs sm:text-sm font-semibold tracking-widest color-primary uppercase">
            {t('subtitle')}
          </span>
          <h2 id="comparison-heading" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
            {t.rich('title', {
              highlight: (chunks) => <span className="color-primary">{chunks}</span>,
              br: () => <br />,
            })}
          </h2>
          <p className="text-sm sm:text-base text-gray-500 leading-relaxed font-normal">
            {t('description')}
          </p>
        </div>

        <div className="lg:col-span-8 overflow-x-auto">
          <table className="w-full text-left border-separate border-spacing-x-2 p-0 md:border-spacing-x-3 border-spacing-y-0 min-w-[500px]">
            <thead>
              <tr>
                <th scope="col" className="py-1.5 px-3 md:py-4 md:px-5 bg-transparent" />
                <th
                  scope="col"
                  className="py-1.5 px-3 md:py-4 md:px-5 text-xs font-bold text-gray-400 uppercase tracking-wider bg-gray-50/50 border border-gray-200 rounded-t-2xl text-center"
                >
                  {t('col_card_payment')}
                </th>
                <th
                  scope="col"
                  className="py-1.5 px-3 md:py-4 md:px-5 text-xs font-bold text-white uppercase tracking-wider bg-primary border-t border-x border-color-primary rounded-t-2xl text-center"
                >
                  {t('col_kuik_qr')}
                </th>
              </tr>
            </thead>
            <tbody className="text-xs sm:text-sm">
              {rows.map((row, idx) => {
                const isLastRow = idx === rows.length - 1;
                const rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-blue-50/50';

                return (
                  <tr key={idx} className="transition-colors hover:bg-gray-50/30">
                    {/* Column 1: Unboxed Row Label Legend */}
                    <td className="py-1.5 px-3 md:py-4 md:px-5 font-bold text-slate-600 flex flex-row gap-2.5 items-center bg-transparent border-none">
                      <span className="text-slate-400 shrink-0">{row.icon}</span>
                      {t(row.feature_key)}
                    </td>

                    {/* Column 2: Card Payment Option */}
                    <td
                      className={`py-1.5 px-3 md:py-4 md:px-5 border-x border-gray-200 ${idx === 2 || idx === 3 ? 'text-red-500 font-semibold' : 'color-secondary-text font-medium'
                        } ${rowBg} ${isLastRow ? 'rounded-b-2xl border-b' : ''}`}
                    >
                      {t(row.card_payment_key)}
                    </td>

                    {/* Column 3: Kuik Highlighted Option */}
                    <td
                      className={`py-1.5 px-3 md:py-4 md:px-5 font-bold border-x-2 border-color-primary ${rowBg} ${isLastRow ? 'rounded-b-2xl border-b-2' : ''
                        } ${row.kuik_highlight ? 'text-emerald-600' : 'color-primary'}`}
                    >
                      {t(row.kuik_payment_key)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </motion.section>
  );
}